import type * as React from "react";

import { ClockIcon } from "./Icons";
import { cn } from "../lib/cn";

function toDate(value: Date | string): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Browser-local time. */
export function formatClockTime(
  date: Date,
  meridiem = false,
  seconds = false,
): string {
  const hours = date.getHours();
  const minutes = `${hours % 12 || 12}:${String(date.getMinutes()).padStart(2, "0")}`;
  const text = seconds
    ? `${minutes}:${String(date.getSeconds()).padStart(2, "0")}`
    : minutes;
  return meridiem ? `${text} ${hours < 12 ? "AM" : "PM"}` : text;
}

export interface TimeLabelProps extends Omit<
  React.TimeHTMLAttributes<HTMLTimeElement>,
  "children" | "dateTime"
> {
  value: Date | string;
  glyph?: boolean;
  /** For live monitors, where a minute is too coarse to compare two times. */
  seconds?: boolean;
  ref?: React.Ref<HTMLTimeElement>;
}

export function TimeLabel({
  value,
  glyph = true,
  seconds = false,
  className,
  ...rest
}: TimeLabelProps) {
  const date = toDate(value);

  return (
    <time
      dateTime={date?.toISOString()}
      className={cn(
        "inline-flex items-center whitespace-nowrap text-mono-time text-sm text-annotation",
        className,
      )}
      {...rest}
    >
      {glyph && <ClockIcon className="mr-[0.35em]" />}
      {date ? formatClockTime(date, false, seconds) : String(value)}
    </time>
  );
}
